import { forwardRef } from 'react';
import { cx } from './cx';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'link';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-lg transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-meradio-primary hover:bg-meradio-primary-dark text-white font-semibold hover:shadow-meradio hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-meradio-primary',
  secondary:
    'bg-meradio-secondary hover:bg-meradio-secondary-dark text-white font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-meradio-secondary',
  outline:
    'border border-meradio-primary text-meradio-primary hover:bg-meradio-primary hover:text-white font-medium focus-visible:ring-meradio-primary',
  danger:
    'bg-danger-600 hover:bg-danger-700 text-white font-semibold focus-visible:ring-danger-500',
  link:
    'text-meradio-primary hover:text-meradio-primary-dark font-medium underline-offset-2 hover:underline focus-visible:ring-meradio-primary',
};

const linkSizes: Record<ButtonSize, string> = {
  sm: 'text-sm',
  md: '',
  lg: 'text-lg',
};

export const buttonClasses = (
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  fullWidth = false
): string =>
  cx(base, variants[variant], (variant === 'link' ? linkSizes : sizes)[size], fullWidth && 'w-full');

const sizes: Record<ButtonSize, string> = {
  sm: 'py-1.5 px-3 text-sm',
  md: 'py-3 px-6',
  lg: 'py-4 px-8 text-lg',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      type = 'button',
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      className={cx(buttonClasses(variant, size, fullWidth), className)}
      {...props}
    >
      {isLoading && (
        <span
          aria-hidden
          className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
        />
      )}
      {children}
    </button>
  )
);

Button.displayName = 'Button';

export default Button;
